/**
 * Unified Route IDs for the Atlas application.
 * This single enum is used by both the main app and country-profiles module.
 */
export enum RouteID {
  /* Content Pages */
  StaticPageEntry = "StaticPage",
  Error = "Error",
  Landing = "Landing",
  Learn = "Learn",
  Glossary = "Glossary",
  FAQ = "FAQ",
  About = "About",
  DataDownloads = "DataDownloads",
  TradeDataMethodology = "TradeDataMethodology",
  TradeDataMethodologyPresentation = "TradeDataMethodologyPresentation",
  AtlasUI = "AtlasUI",
  PackageDocs = "PackageDocs",

  /* Visualization routes (new app) */
  Explore = "Explore",
  MobileExplore = "MobileExplore",
  ExploreDefault = "ExploreDefault",
  GeoMap = "GeoMap",
  OverTime = "OverTime",
  MarketShare = "MarketShare",
  Feasibility = "Feasibility",
  RingsGraph = "RingsGraph",
  TreeMap = "TreeMap",
  ProductSpace = "ProductSpace",

  /* Country Profiles routes */
  Home = "Home",
  CountryProfiles = "CountryProfiles",
  CountryRankings = "CountryRankings",
  ProductRankings = "ProductRankings",
  GrowthProjections = "GrowthProjections",
  GrowthProjectionsPressRelease = "GrowthProjectionsPressRelease",

  /* External/About sub-routes (used for navigation links) */
  AboutData = "AboutData",
  DataPermissions = "DataPermissions",
  WhatIsTheAtlas = "WhatIsTheAtlas",
  OurTeam = "OurTeam",
  MediaOutreach = "MediaOutreach",
  GrowthLab = "GrowthLab",
  SupportMission = "SupportMission",
  ContactUs = "ContactUs",
}

export enum AboutRouteID {
  // About Page hash routes
  WhatIsTheAtlas = "what-is-the-atlas",
  OurTeam = "our-team",
  MediaOutreach = "media-outreach",
  GrowthLab = "growth-lab",
  SupportMission = "support-mission",
  ContactUs = "contact-us",
}

// ============================================
// Route Paths (for URL generation)
// ============================================

// Partial routePaths for country-profiles internal navigation
// Using Partial<Record<...>> since not all RouteIDs have paths here
export const routePaths: Partial<Record<RouteID, string>> = {
  // Home now redirects to country profiles (rewrite handles /)
  [RouteID.Home]: "/countries",

  // Country profiles routes
  [RouteID.CountryProfiles]: "/countries/:country?/:slide?",
  [RouteID.CountryRankings]: "/rankings/:year?",
  [RouteID.ProductRankings]: "/rankings/product/:year?",
  [RouteID.GrowthProjections]: "/rankings/growth-projections",
  [RouteID.GrowthProjectionsPressRelease]: "/press-release",

  // Visualization routes (explore section)
  [RouteID.Explore]: "/explore",
  [RouteID.TreeMap]: "/explore/treemap",
  [RouteID.GeoMap]: "/explore/geomap",
  [RouteID.OverTime]: "/explore/overtime",
  [RouteID.MarketShare]: "/explore/marketshare",
  [RouteID.ProductSpace]: "/explore/productspace",
  [RouteID.Feasibility]: "/explore/feasibility",

  // External routes - full paths for link generation
  [RouteID.DataDownloads]: "/data-downloads",
  [RouteID.AboutData]: "/about-data",
  [RouteID.DataPermissions]: "/data-use-permissions",
  [RouteID.Glossary]: "/glossary",
  [RouteID.FAQ]: "/faq",
  [RouteID.TradeDataMethodology]: "/trade-data-methodology",
  [RouteID.WhatIsTheAtlas]: "/about#what-is-the-atlas",
  [RouteID.OurTeam]: "/about#our-team",
  [RouteID.MediaOutreach]: "/about#media-outreach",
  [RouteID.GrowthLab]: "/about#growth-lab",
  [RouteID.SupportMission]: "/about#support-mission",
  [RouteID.ContactUs]: "/about#contact-us",
  [RouteID.About]: "/about",
  [RouteID.TradeDataMethodologyPresentation]:
    "/trade-data-methodology/presentation",
  [RouteID.AtlasUI]: "/atlas-ui",
};

// ============================================
// Route parameter names
// ============================================
export const countryProfilesCountryParamName = "country";
export const countryProfilesSlideParamName = "slide";
export const countryRankingsYearParamName = "year";
export const productRankingsYearParamName = "year";
